// <auto-generated>
// This file is automatically generated from DSL.
// Source: dsl/entities/DimensionDefinition.yaml
// Generated: 2026-05-31T18:49:04.287Z
// </auto-generated>

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { dimensionDefinitionService } from '@/services/finance/DimensionDefinitionService';
import { CreateDimensionDefinition, UpdateDimensionDefinition, DimensionDefinition } from '@/types/finance/DimensionDefinition';
import { authService } from '@/services/auth/authService';

/**
 * Query key factory for DimensionDefinition
 * Includes tenant ID to ensure different tenants have separate caches
 */
export const dimensiondefinitionKeys = {
  all: (tenantId?: string | null) => ['dimensiondefinitions', tenantId] as const,
  lists: (tenantId?: string | null) => [...dimensiondefinitionKeys.all(tenantId), 'list'] as const,
  list: (tenantId?: string | null, filters?: any) => [...dimensiondefinitionKeys.lists(tenantId), { filters }] as const,
  details: (tenantId?: string | null) => [...dimensiondefinitionKeys.all(tenantId), 'detail'] as const,
  detail: (id: string, tenantId?: string | null) => [...dimensiondefinitionKeys.details(tenantId), id] as const,
};

/**
 * Hook to fetch all DimensionDefinitions
 */
export function useDimensionDefinitions(options?: Omit<UseQueryOptions<DimensionDefinition[], Error>, 'queryKey' | 'queryFn'>) {
  // Get current tenant ID for cache key
  const tenantId = authService.getTenantId();

  return useQuery<DimensionDefinition[], Error>({
    queryKey: dimensiondefinitionKeys.lists(tenantId),
    queryFn: () => dimensionDefinitionService.getAll(),
    ...options
  });
}

/**
 * Hook to fetch a single DimensionDefinition by ID
 */
export function useDimensionDefinition(
  id: string,
  options?: Omit<UseQueryOptions<DimensionDefinition | undefined, Error>, 'queryKey' | 'queryFn'>
) {
  const tenantId = authService.getTenantId();

  return useQuery<DimensionDefinition | undefined, Error>({
    queryKey: dimensiondefinitionKeys.detail(id, tenantId),
    queryFn: () => dimensionDefinitionService.getById(id),
    enabled: !!id && id !== 'new',
    ...options
  });
}

/**
 * Hook to create a new DimensionDefinition
 */
export function useCreateDimensionDefinition() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<DimensionDefinition, Error, CreateDimensionDefinition, { previousDimensionDefinitions?: DimensionDefinition[] }>({
    mutationFn: (data: CreateDimensionDefinition) => dimensionDefinitionService.create(data),
    onMutate: async (newDimensionDefinition) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: dimensiondefinitionKeys.lists(tenantId) });

      // Snapshot previous value
      const previousDimensionDefinitions = queryClient.getQueryData<DimensionDefinition[]>(dimensiondefinitionKeys.lists(tenantId));

      // Optimistically update to the new value. Spread the input first so a
      // client-supplied id (offline / idempotency key) wins over the temp one.
      queryClient.setQueryData<DimensionDefinition[]>(dimensiondefinitionKeys.lists(tenantId), (old = []) => [
        ...old,
        { ...newDimensionDefinition, id: (newDimensionDefinition as { id?: string }).id ?? 'temp-' + Date.now() } as DimensionDefinition
      ]);

      return { previousDimensionDefinitions };
    },
    onError: (_err, _newDimensionDefinition, context) => {
      // Rollback on error
      if (context?.previousDimensionDefinitions) {
        queryClient.setQueryData(dimensiondefinitionKeys.lists(tenantId), context.previousDimensionDefinitions);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: dimensiondefinitionKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to update an existing DimensionDefinition
 */
export function useUpdateDimensionDefinition() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<DimensionDefinition, Error, { id: string; data: UpdateDimensionDefinition }, { previousDimensionDefinition?: DimensionDefinition; previousDimensionDefinitions?: DimensionDefinition[] }>({
    mutationFn: ({ id, data }) => dimensionDefinitionService.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: dimensiondefinitionKeys.detail(id, tenantId) });
      await queryClient.cancelQueries({ queryKey: dimensiondefinitionKeys.lists(tenantId) });

      // Snapshot previous values
      const previousDimensionDefinition = queryClient.getQueryData<DimensionDefinition>(dimensiondefinitionKeys.detail(id, tenantId));
      const previousDimensionDefinitions = queryClient.getQueryData<DimensionDefinition[]>(dimensiondefinitionKeys.lists(tenantId));

      // Optimistically update detail
      queryClient.setQueryData<DimensionDefinition>(dimensiondefinitionKeys.detail(id, tenantId), (old) => ({
        ...old!,
        ...data
      }) as DimensionDefinition);

      // Optimistically update list
      queryClient.setQueryData<DimensionDefinition[]>(dimensiondefinitionKeys.lists(tenantId), (old = []) =>
        old.map((item) => (item.id === id ? { ...item, ...data } as DimensionDefinition : item))
      );

      return { previousDimensionDefinition, previousDimensionDefinitions };
    },
    onError: (_err, { id }, context) => {
      // Rollback on error
      if (context?.previousDimensionDefinition) {
        queryClient.setQueryData(dimensiondefinitionKeys.detail(id, tenantId), context.previousDimensionDefinition);
      }
      if (context?.previousDimensionDefinitions) {
        queryClient.setQueryData(dimensiondefinitionKeys.lists(tenantId), context.previousDimensionDefinitions);
      }
    },
    onSuccess: (_, { id }) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: dimensiondefinitionKeys.detail(id, tenantId) });
      queryClient.invalidateQueries({ queryKey: dimensiondefinitionKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to delete a DimensionDefinition
 */
export function useDeleteDimensionDefinition() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<void, Error, string, { previousDimensionDefinitions?: DimensionDefinition[] }>({
    mutationFn: (id: string) => dimensionDefinitionService.delete(id),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: dimensiondefinitionKeys.lists(tenantId) });

      // Snapshot previous value
      const previousDimensionDefinitions = queryClient.getQueryData<DimensionDefinition[]>(dimensiondefinitionKeys.lists(tenantId));

      // Optimistically remove from list
      queryClient.setQueryData<DimensionDefinition[]>(dimensiondefinitionKeys.lists(tenantId), (old = []) =>
        old.filter((item) => item.id !== id)
      );

      return { previousDimensionDefinitions };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousDimensionDefinitions) {
        queryClient.setQueryData(dimensiondefinitionKeys.lists(tenantId), context.previousDimensionDefinitions);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: dimensiondefinitionKeys.lists(tenantId) });
    }
  });
}

/**
 * Combined hook for save (create or update)
 * Automatically determines whether to create or update based on presence of ID
 */
export function useSaveDimensionDefinition() {
  const createMutation = useCreateDimensionDefinition();
  const updateMutation = useUpdateDimensionDefinition();

  return {
    mutate: (params: { id?: string; data: CreateDimensionDefinition | UpdateDimensionDefinition }, options?: any) => {
      if (params.id && params.id !== 'new') {
        return updateMutation.mutate({ id: params.id, data: params.data as UpdateDimensionDefinition }, options);
      } else {
        return createMutation.mutate(params.data as CreateDimensionDefinition, options);
      }
    },
    isPending: createMutation.isPending || updateMutation.isPending,
    isError: createMutation.isError || updateMutation.isError,
    error: createMutation.error || updateMutation.error
  };
}
